import React, { useState, useEffect } from 'react';
import Dashboard from './components/Dashboard';
import SettingsPanel from './components/SettingsPanel';
import AuthForm from './components/AuthForm';
import { authAPI } from './services/api';
import './index.css';
import AITimelineEditor from './components/AITimelineEditor';
import VideoUploader from './components/VideoUploader'; 

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'uploader', 'editor'
  const [selectedProject, setSelectedProject] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const currentUser = await authAPI.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        // Not logged in
      }
      setLoading(false);
    };
    checkUser();
  }, []);

  const handleLogin = (user) => {
    setUser(user);
  };

  const handleLogout = async () => {
    await authAPI.logout();
    setUser(null);
  };

  const handleNewProject = (project) => {
    console.log('Creating new project:', project);
    setCurrentView('editor');
    setSelectedProject({ 
      ...project, 
      isNew: true 
    });
  };

  const handleUploadComplete = (uploadedFile) => {
    console.log('Upload completed:', uploadedFile);
    setCurrentView('editor');
    setSelectedProject({ 
      ...selectedProject, 
      uploadedFile, 
      isNew: true 
    });
  };

  const handleProjectSelect = (project) => {
    console.log('Selected project:', project);
    setCurrentView('editor');
    setSelectedProject(project);
  };

  const handleShowSettings = () => {
    setShowSettings(true);
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-screen bg-black">
          <div className="text-white">Loading...</div>
        </div>
      );
    }

    if (!user) {
      // TEMPORARY: Bypass authentication for testing with better UX
      setTimeout(() => {
        setUser({ 
          id: 'demo-user', 
          full_name: 'Demo User', 
          email: 'demo@example.com',
          role: 'Pro User',
          is_verified: true,
          total_edits: 42,
          total_exports: 18,
          settings: {
            default_caption_style: 'white',
            default_caption_position: 'bottom',
            default_export_resolution: '1080p',
            default_export_format: 'mp4',
            timeline_view: 'horizontal',
            theme: 'dark',
            auto_save_interval: 10,
            email_notifications: true,
            export_complete_notifications: true
          },
          storage_usage: {
            used_mb: 156.8,
            limit_mb: 2048,
            project_count: 8
          }
        });
      }, 800);
      
      return (
        <div className="flex items-center justify-center h-screen bg-black">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-white text-lg font-medium">Welcome to FnctionOS</div>
            <div className="text-gray-400 text-sm mt-2">Setting up your workspace...</div>
          </div>
        </div>
      );
    }

    if (currentView === 'uploader') {
      return <VideoUploader onUploadComplete={handleUploadComplete} />;
    }

    if (currentView === 'editor') {
      return <AITimelineEditor project={selectedProject} onBack={() => setCurrentView('dashboard')} />;
    }

    return (
      <>
        <Dashboard 
          user={user} 
          onLogout={handleLogout} 
          onNewProject={handleNewProject}
          onShowSettings={handleShowSettings}
          onProjectSelect={handleProjectSelect}
        />
        {showSettings && (
          <SettingsPanel
            user={user}
            onClose={handleCloseSettings}
            onLogout={handleLogout}
            onUserUpdate={handleUserUpdate}
          />
        )}
      </>
    );
  };

  return (
    <div className="App">
      {renderContent()}
    </div>
  );
}

export default App;